import { z } from 'zod/v4';

import { foldSynonyms, isString } from './fold-helpers';
import { normalizeWidgetArgs } from './normalize-widget-args';
import { WIDGET_ID_SYNONYM_KEYS } from './remove-widget.schema';

/**
 * Type-agnostic update args — `data` is deliberately an open record.
 *
 * This is the one tool whose schema cannot describe its own payload: the valid fields depend on the
 * widget's type, and the point of the tool is that the caller knows only the id. Validation is not
 * skipped, only moved — `apps/mcp-backend` reads the type off the STORED widget and validates the
 * patch against that type's real schema, so a wrong-shaped patch fails loudly with a message naming
 * both the type and the offending fields, which is enough for the model to correct itself on the
 * next call.
 *
 * The trade-off is that a model calling this blind gets no field guidance up front, which is exactly
 * why the tool's description points at the type-specific tools whenever the type IS known.
 */
const updateWidgetObjectSchema = z.object({
    widgetId: z.string().describe('Id of the widget to update.'),
    data: z
        .record(z.string(), z.unknown())
        .describe(
            'Patch for the widget. Which fields are valid depends on the widget type, and the server ' +
                'validates against the real schema for whatever type this widget turns out to be — if you get ' +
                'it wrong, the error names the type and the valid fields, so read it and retry.'
        ),
});

export const updateWidgetArgsSchema = z.preprocess(
    (raw) =>
        // Reuses the shared `{ …, data }` scaffolding for the JSON-string `data` case (the leaked-XML
        // transport always sends strings) and for loud rejection of a malformed `data`. The fold does
        // nothing to `data` itself — there are no per-type synonyms to apply when the type is unknown
        // — it only lifts a flat `id` onto `widgetId`.
        normalizeWidgetArgs(
            raw,
            (args, data) => {
                foldSynonyms(args, args, 'widgetId', WIDGET_ID_SYNONYM_KEYS, isString);
                return data;
            },
            WIDGET_ID_SYNONYM_KEYS
        ),
    updateWidgetObjectSchema
);
